import { getMbFileLimit } from '@payaca/helpers/fileHelper';
import { UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP } from '@payaca/types/uploadTypes';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import Alert from '../Alert/Alert';
import UntitledLoading from '../UntitledLoading/UntitledLoading';
import FileDropIcon from './FileDropIcon.svg?react';

export type FileDropZoneProps = {
  className?: string;
  isLoading?: boolean;
  hasError?: boolean;
  dropzoneOptions?: DropzoneOptions;
  remainingSlots?: number;
};

const FileDropZone: FC<FileDropZoneProps> = (props) => {
  const {
    className,
    isLoading = false,
    hasError = false,
    dropzoneOptions,
    remainingSlots,
  } = props;

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      disabled: dropzoneOptions?.disabled || isLoading,
      maxFiles: remainingSlots,
      ...dropzoneOptions,
    });

  const rejectionError = fileRejections.find((rejection) =>
    rejection.errors.some((error) => error.code === 'too-many-files')
  );

  // Accept value can either be a string or an array of strings
  const acceptValue = dropzoneOptions?.accept;
  const fileTypesCleaned = acceptValue
    ? (Array.isArray(acceptValue) ? acceptValue.join(', ') : acceptValue)
        .replaceAll('.', ' ')
        .toUpperCase()
    : null;

  return (
    <div
      className={clstx(
        'relative m-auto cursor-pointer rounded-xl border border-dashed p-10',
        isDragActive
          ? 'border-blue-600 bg-blue-50'
          : 'border-gray-300 bg-white',
        className
      )}
      {...getRootProps()}
    >
      <div
        className={
          'typography:prose flex flex-col items-center text-center' +
          (isLoading ? ' invisible' : '')
        }
      >
        <input {...getInputProps()} />

        {hasError && (
          <Alert className="mb-4" colour="red">
            An error occurred - please try again later
          </Alert>
        )}

        {rejectionError && (
          <Alert className="mb-4" colour="red">
            You can only select a maximum of {dropzoneOptions?.maxFiles} files
            at once.
          </Alert>
        )}

        <FileDropIcon className="mb-4" />
        <h4 className="mb-1">
          Drop your file
          {dropzoneOptions?.maxFiles === 1 ? '' : 's'} here or{' '}
          <span className="link-body">browse</span>
        </h4>
        {!!fileTypesCleaned && (
          <p className="supporting-body text-gray-500 mb-1">
            {fileTypesCleaned}
          </p>
        )}
        <p className="supporting-body text-gray-500">
          Maximum size: {getMbFileLimit(UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP)}
        </p>
      </div>

      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <UntitledLoading className="h-6 w-6" />
        </div>
      ) : null}
    </div>
  );
};

export default FileDropZone;
